import { useCallback, useEffect, useState } from 'react';

import { GraphQLError } from 'graphql';
import { useIntl } from 'react-intl';

import {
  IPrepaidsMergeInput,
  IPrepaidsMergeMutation,
  UserAccountsDocument,
  useDeleteAccountMutation,
  useUserAccountsQuery
} from 'generated/rbi-graphql';
import { usePrepaidsReload } from 'hooks/prepaid';
import { getNonce, mergePrepaidCards } from 'remote/api/first-data';
import { HttpErrorCodes } from 'remote/constants';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { CustomEventNames, EventTypes } from 'state/mParticle';
import { StatusType, dataDogLogger } from 'utils/datadog';
import { storePrepaidCard } from 'utils/encryption';
import { ISOs } from 'utils/form/constants';

import {
  IAddPaymentMethodInput,
  IAddPaymentMethodOptions,
  IPaymentMethod,
  IReloadPrepaidCard,
  PaymentProcessor
} from '../types';

import { getPaymentMethodsState } from './getPaymentMethodsState';
import { messages } from './messages';
import { IInitPaymentMethods, IPayment } from './types';

const usePayment = ({
  getEncryptionDetailsMutation,
  mParticle,
  openErrorDialog,
  query,
  user,
  updateUserInfo,
  addAccountMutation,
  addCreditAccountMutation
}: IPayment) => {
  const logger = useLogger();
  const { formatMessage } = useIntl();
  const { feCountryCode } = useLocale();
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [hasGetPaymentMethodsError, setHasGetPaymentMethodsError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const { canUseApplePay, applePayCardDetails } = useApplePay({});
  const { canUseGooglePay, googlePayCardDetails } = useGooglePay();
  const enableThDirectPayApplePay = useFlag(LaunchDarklyFlag.ENABLE_TH_DIRECT_PAY_APPLE_PAY);
  const enableThDirectPayGooglePay = useFlag(LaunchDarklyFlag.ENABLE_TH_DIRECT_PAY_GOOGLE_PAY);
  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);
  const [fireDeleteAccountMutation] = useDeleteAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }]
  });

  const {
    data: userAccountsData,
    loading: accountsLoading,
    refetch: refetchPaymentMethods
  } = useUserAccountsQuery({
    variables: { feCountryCode },
    // Apollo client loading state gets stuck: https://github.com/apollographql/react-apollo/issues/3425
    // Temporary fix while we wait for a stable 3.0.0 version
    fetchPolicy: 'cache-and-network',
    skip: !user
  });

  const { cognitoId, thLegacyCognitoId, details } = user || {};
  const {
    // temporary rename, eventually `defaultAccountIdentifier` will exist in the `details` object when the backend is updated
    defaultFdAccountId: defaultAccountIdentifier,
    defaultPaymentAccountId: defaultPrepaidPaymentMethodId,
    defaultReloadAmt
  } = details || {};

  const [checkoutPaymentMethodId, setCheckoutPaymentMethodId] = useState('');
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState('');
  const [defaultReloadPaymentMethodId, setDefaultReloadPaymentMethodId] = useState('');
  const [prepaidReloadPaymentMethodId, setPrepaidReloadPaymentMethodId] = useState('');
  const [paymentMethodHasBeenInit, setPaymentMethodHasBeenInit] = useState(false);

  const saveNewPaymentMethodEvent = useCallback(
    (success: boolean, message?: string, context = {}) => {
      /// Log to mParticle
      mParticle.logEvent(CustomEventNames.SAVE_NEW_PAYMENT_METHOD, EventTypes.Other, {
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message,
        'Payment Processor': context.paymentProcessor
      });
      // Log to DataDog
      dataDogLogger({
        message: success ? 'Payment method added' : 'Error adding payment method',
        context,
        status: success ? StatusType.info : StatusType.error
      });
    },
    [mParticle]
  );

  const deletePaymentMethodEvent = useCallback(
    (success: boolean, message?: string) => {
      mParticle.logEvent(CustomEventNames.DELETE_PAYMENT_METHOD, EventTypes.Other, {
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message
      });
    },
    [mParticle]
  );

  const logUserPaymentIdentity = useCallback(
    (accountIdentifier: string) => {
      if (!user || !details) {
        return;
      }
      const selectedMethod = paymentMethods.find(
        method =>
          method.accountIdentifier === accountIdentifier || method.fdAccountId === accountIdentifier
      );
      if (!selectedMethod) {
        return;
      }
      let ccToken;
      if (selectedMethod.credit) {
        ccToken = selectedMethod.credit.panToken;
      } else if (selectedMethod.prepaid) {
        ccToken = selectedMethod.prepaid.cardNumber;
      }
      const customerid = thLegacyCognitoId ? `us-east-1:${thLegacyCognitoId}` : cognitoId;
      mParticle.updateUserIdentities({
        customerid,
        email: details.email,
        ccToken
      });
    },
    [mParticle, cognitoId, thLegacyCognitoId, details, paymentMethods, user]
  );

  const setAndLogCheckoutPaymentMethodId = useCallback(
    (fdAccountId: string) => {
      logUserPaymentIdentity(fdAccountId);
      setCheckoutPaymentMethodId(fdAccountId);
    },
    [logUserPaymentIdentity, setCheckoutPaymentMethodId]
  );

  const setAndLogDefaultReloadPaymentMethodId = useCallback(
    (fdAccountId: string) => {
      setPrepaidReloadPaymentMethodId(fdAccountId);
      setDefaultReloadPaymentMethodId(fdAccountId);
      if (user) {
        updateUserInfo({
          ...user.details,
          defaultReloadPaymentMethodId: fdAccountId
        });
      }
    },
    [user, updateUserInfo]
  );

  const setAndLogDefaultPaymentMethodId = useCallback(
    (fdAccountId: string, isAddingNewCc?: boolean) => {
      logUserPaymentIdentity(fdAccountId);
      setDefaultPaymentMethodId(fdAccountId);
      // Set the checkout payment method when the user change its default one
      setCheckoutPaymentMethodId(fdAccountId);

      if (user) {
        // Update the checkout and reload payment method when adding a new CC
        updateUserInfo({
          ...user.details,
          defaultCheckoutPaymentMethodId: fdAccountId,
          ...(isAddingNewCc ? { defaultReloadPaymentMethodId: fdAccountId } : {})
        });
      }
    },
    [logUserPaymentIdentity, setDefaultPaymentMethodId, user, updateUserInfo]
  );

  const getEncryptionDetails = useCallback(async () => {
    const encryptionDetailsResponse = await getEncryptionDetailsMutation();

    if (!encryptionDetailsResponse.data) {
      throw new Error('Missing encryption details');
    }

    return encryptionDetailsResponse.data.encryptionDetails;
  }, [getEncryptionDetailsMutation]);

  const clearPaymentMethods = useCallback(() => {
    setPaymentMethods([]);
    setHasGetPaymentMethodsError(false);
    setLoading(false);
  }, []);

  const getPaymentMethods = useCallback(async () => {
    try {
      if (cognitoId) {
        await refetchPaymentMethods();
      }
    } catch (error) {
      logger.error({
        error,
        message: 'Error getting payment methods'
      });
      clearPaymentMethods();
      setHasGetPaymentMethodsError(true);
      openErrorDialog({
        message: formatMessage(messages.paymentLoadingError),
        modalAppearanceEventMessage: 'Error: Fetching Payment Methods Failure',
        error
      });
    }
  }, [
    refetchPaymentMethods,
    logger,
    clearPaymentMethods,
    openErrorDialog,
    formatMessage,
    cognitoId
  ]);

  const initPaymentMethods = useCallback(
    ({ accounts, paymentMethodId, prepaidPaymentMethodId }: IInitPaymentMethods) => {
      // Get valid payment methods
      const {
        availablePaymentMethodList,
        validDefaultPaymentMethodId,
        validCheckoutPaymentMethodId,
        validReloadPaymentMethodId,
        validDefaultReloadPaymentMethodId
      } = getPaymentMethodsState({
        paymentMethodId,
        prepaidPaymentMethodId,
        accounts,
        canUseApplePay,
        enableThDirectPayApplePay,
        applePayCardDetails,
        canUseGooglePay,
        enableThDirectPayGooglePay,
        googlePayCardDetails,
        paymentMethodHasBeenInit
      });
      // Setup the payment methods into a valid state
      setPaymentMethods(availablePaymentMethodList);
      setDefaultPaymentMethodId(validDefaultPaymentMethodId);
      setCheckoutPaymentMethodId(validCheckoutPaymentMethodId);
      setDefaultReloadPaymentMethodId(validDefaultReloadPaymentMethodId);
      setPrepaidReloadPaymentMethodId(validReloadPaymentMethodId);
    },
    [
      applePayCardDetails,
      canUseApplePay,
      canUseGooglePay,
      enableThDirectPayApplePay,
      enableThDirectPayGooglePay,
      googlePayCardDetails,
      paymentMethodHasBeenInit
    ]
  );

  useEffect(() => {
    if (!user) {
      clearPaymentMethods();
      return;
    }
    if (userAccountsData) {
      const accounts: IPaymentMethod[] =
        (userAccountsData.userAccounts &&
          userAccountsData.userAccounts.accounts &&
          userAccountsData.userAccounts.accounts.filter<IPaymentMethod>(
            (account): account is IPaymentMethod =>
              !!account && (!!account.fdAccountId || !!account.accountIdentifier)
          )) ||
        [];

      setHasGetPaymentMethodsError(false);

      if (accounts.length) {
        storePrepaidCard({ accounts });
      }
      setLoading(false);
    }
  }, [clearPaymentMethods, user, userAccountsData]);

  const { reloadPrepaidsCardMutation, loading: prepaidLoading } = usePrepaidsReload({
    openErrorDialog
  });

  const getPrepaidPaymentMethod = useCallback(() => {
    return paymentMethods.find(method => Boolean(method.prepaid)) || null;
  }, [paymentMethods]);

  const getBalanceFromPaymentMethods = (prepaidPaymentMethod: IPaymentMethod) => {
    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      if (prepaidPaymentMethod.prepaid.feFormattedCurrentBalance) {
        return prepaidPaymentMethod.prepaid.feFormattedCurrentBalance;
      } else if (prepaidPaymentMethod.prepaid.currentBalance) {
        return prepaidPaymentMethod.prepaid.currentBalance;
      }
    }
    return 0;
  };

  const getPrepaidCardNumber = useCallback(() => {
    const prepaidPaymentMethod = getPrepaidPaymentMethod();

    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      // add spaces between every 4 characters in the card number
      return (prepaidPaymentMethod.prepaid.cardNumber.match(/.{1,4}/g) || []).join(' ');
    }
    return null;
  }, [getPrepaidPaymentMethod]);

  const deletePaymentMethod = useCallback(
    async (accountIdentifier: string) => {
      try {
        const { data } = await fireDeleteAccountMutation({
          variables: { input: { accountIdentifier } }
        });
        if (data?.deleteAccount) {
          // Strip the paymentMethods list from the deleted CC
          const updatedPaymentMethods = paymentMethods.filter(
            method => method.accountIdentifier !== accountIdentifier
          );
          // If the default payment method is the deleted one, reset the payment method using the 1st in the updated array
          const paymentMethodIdAfterDelete =
            defaultAccountIdentifier !== accountIdentifier
              ? defaultAccountIdentifier
              : updatedPaymentMethods[0]?.accountIdentifier;

          // Re-init the payment methods state to make sure we're in a fully working state after deleting a CC
          initPaymentMethods({
            accounts: updatedPaymentMethods,
            paymentMethodId: paymentMethodIdAfterDelete || '',
            prepaidPaymentMethodId: paymentMethodIdAfterDelete || ''
          });

          deletePaymentMethodEvent(true);
        }
      } catch (error) {
        logger.error({ error, message: 'Error deleting payment method' });
        deletePaymentMethodEvent(false, error.message);
        openErrorDialog({
          message: formatMessage(messages.paymentDeletingError),
          modalAppearanceEventMessage: 'Error: Deleting Payment Method Failure',
          error
        });
      }
    },
    [
      fireDeleteAccountMutation,
      paymentMethods,
      defaultAccountIdentifier,
      initPaymentMethods,
      deletePaymentMethodEvent,
      logger,
      openErrorDialog,
      formatMessage
    ]
  );

  const paymentProcessor = userAccountsData?.userAccounts?.paymentProcessor;
  const isAdyen = paymentProcessor === PaymentProcessor.ADYEN;

  const addPaymentMethod = useCallback(
    async (
      paymentValues: IAddPaymentMethodInput,
      { skipErrorDialogOnError = false }: IAddPaymentMethodOptions = {}
    ) => {
      const { billingAddress, securityCode, cardNumber, nameOnCard: fullName } = paymentValues;
      const { accountToDelete, chaseProfileId, ...cleanedPaymentValues } = paymentValues;
      const ccBin = cardNumber.slice(0, 6);

      if (!loading) {
        setLoading(true);
      }

      try {
        const { fdPublicKey, fdApiKey, fdAccessToken, fdCustomerId } = await getEncryptionDetails();
        const nonceResponse = await getNonce(
          cleanedPaymentValues,
          fdPublicKey,
          fdApiKey,
          fdAccessToken,
          fdCustomerId,
          details?.isoCountryCode as ISOs | undefined,
          onlySendPostalCode
        );
        const { token } = await nonceResponse.json();

        const fdNonce = token.tokenId;

        // If we're re-vaulting, delete the payment method before adding the new one
        if (accountToDelete) {
          await deletePaymentMethod(accountToDelete);
        }

        const addAccountResponse = await addAccountMutation({
          variables: {
            input: {
              billingAddress,
              fdAccessToken,
              fdNonce,
              ccBin,
              fullName,
              chaseProfileId,
              cvv: securityCode
            }
          }
        });

        if (
          !addAccountResponse.data ||
          (!addAccountResponse.data.addAccount.accountIdentifier &&
            !addAccountResponse.data.addAccount.fdAccountId)
        ) {
          throw new Error('Add Account Failure');
        }

        saveNewPaymentMethodEvent(true, undefined, { chaseProfileId, paymentProcessor });
        const { addAccount } = addAccountResponse.data;
        const accountIdentifier = addAccount.accountIdentifier ?? addAccount.fdAccountId ?? '';

        // Re-init the payment methods state to make sure we're in a fully working state after adding a CC
        initPaymentMethods({
          accounts: [...paymentMethods, addAccount as IPaymentMethod],
          paymentMethodId: accountIdentifier,
          prepaidPaymentMethodId: accountIdentifier
        });

        // When a user adds a CC, set the Checkout and Reload Default payment method to the newly added one
        await setAndLogDefaultPaymentMethodId(accountIdentifier, true);

        return accountIdentifier;
      } catch (error) {
        logger.error({
          error,
          message: 'Error adding payment method'
        });
        saveNewPaymentMethodEvent(false, error.message, {
          chaseProfileId,
          paymentProcessor,
          message: error.message
        });
        if (!skipErrorDialogOnError) {
          const statusCode:
            | number
            | undefined = error.graphQLErrors?.find((graphQLError: GraphQLError) =>
            Number.isInteger(graphQLError?.extensions?.statusCode)
          )?.extensions?.statusCode;
          openErrorDialog({
            message:
              statusCode === HttpErrorCodes.TooManyRequests
                ? formatMessage(messages.tooManyAddAccountAttempts)
                : formatMessage(messages.paymentAddingError),
            modalAppearanceEventMessage: 'Error: Adding Payment Method Failure',
            error
          });
        }

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      loading,
      getEncryptionDetails,
      details,
      addAccountMutation,
      saveNewPaymentMethodEvent,
      initPaymentMethods,
      paymentMethods,
      setAndLogDefaultPaymentMethodId,
      deletePaymentMethod,
      logger,
      openErrorDialog,
      formatMessage,
      onlySendPostalCode,
      paymentProcessor
    ]
  );

  const addAdyenPaymentMethod = useCallback(
    async (paymentValues: IAddPaymentMethodInput) => {
      const {
        billingAddress,
        securityCode,
        expiryDate,
        cardNumber,
        nameOnCard: fullName
      } = paymentValues;

      if (!loading) {
        setLoading(true);
      }

      try {
        const addAccountResponse = await addCreditAccountMutation({
          variables: {
            input: {
              adyenInput: {
                encryptedPayload: {
                  encryptedCN: cardNumber,
                  encryptedEM: expiryDate.month,
                  encryptedEY: expiryDate.year,
                  encryptedSC: securityCode
                }
              },
              billingAddress,
              fullName
            }
          }
        });

        if (
          !addAccountResponse.data ||
          !addAccountResponse.data.addCreditAccount.accountIdentifier
        ) {
          throw new Error('Add Account Failure');
        }

        const { addCreditAccount } = addAccountResponse.data;
        const accountIdentifier = addCreditAccount.accountIdentifier ?? '';
        saveNewPaymentMethodEvent(true, undefined, { accountIdentifier, paymentProcessor });

        // Re-init the payment methods state to make sure we're in a fully working state after adding a CC
        initPaymentMethods({
          accounts: [...paymentMethods, addCreditAccount as IPaymentMethod],
          paymentMethodId: accountIdentifier,
          prepaidPaymentMethodId: accountIdentifier
        });

        // When a user adds a CC, set the Checkout and Reload Default payment method to the newly added one
        await setAndLogDefaultPaymentMethodId(accountIdentifier, true);

        return accountIdentifier;
      } catch (error) {
        saveNewPaymentMethodEvent(false, error.message, {
          accountIdentifier: undefined,
          paymentProcessor,
          message: error.message
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      loading,
      addCreditAccountMutation,
      initPaymentMethods,
      paymentMethods,
      saveNewPaymentMethodEvent,
      setAndLogDefaultPaymentMethodId,
      paymentProcessor
    ]
  );

  const reloadPrepaidCard = useCallback(
    async (reloadInfo: IReloadPrepaidCard) => {
      try {
        const { currencyCode, cardType, fundValue } = reloadInfo;

        // TODO: Add the paymentMethodData obj on the BE interface for IApplePayDetails & IGooglePayDetails and remove this deletion
        if (reloadInfo?.applePayDetails?.paymentMethodData) {
          delete reloadInfo.applePayDetails.paymentMethodData;
        }

        const { data } = await reloadPrepaidsCardMutation(reloadInfo);

        mParticle.logEvent(CustomEventNames.BUTTON_CLICK_TIMS_CARD_RELOAD, EventTypes.Transaction, {
          Currency: currencyCode,
          'Card Type': cardType,
          'Reload Amount': fundValue / 100
        });

        return data?.prepaidsReload?.currentBalance;
      } catch (error) {
        logger.error({
          error,
          message: 'Error reloading prepaid card'
        });
        return undefined;
      }
    },
    [logger, mParticle, reloadPrepaidsCardMutation]
  );

  const mergePrepaidCardBalances = async ({
    destinationFdAccountId,
    sourceCardNumber,
    sourcePin
  }: IPrepaidsMergeInput) => {
    if (!loading) {
      setLoading(true);
    }

    try {
      const data: IPrepaidsMergeMutation['prepaidsMerge'] = await query(
        mergePrepaidCards({
          destinationFdAccountId,
          sourceCardNumber: sourceCardNumber.replace(/\s/g, ''),
          sourcePin
        })
      );

      await getPaymentMethods();
      return data;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize the payment method when the app loads
    if (userAccountsData?.userAccounts?.accounts) {
      // First time running, set the paymentMethodHasBeenInit to true
      // This prevent re-init when the user's accounts change
      const { accounts } = userAccountsData.userAccounts;
      if (!paymentMethodHasBeenInit) {
        setPaymentMethodHasBeenInit(true);
        // When we have the user's payment method list, initialize the payment methods state
        initPaymentMethods({
          accounts: accounts as IPaymentMethod[],
          paymentMethodId: defaultAccountIdentifier || '',
          prepaidPaymentMethodId: defaultPrepaidPaymentMethodId || ''
        });
      } else {
        // If the payment methods has been initialized, used the checkoutPaymentMethodId & the prepaidReloadPaymentMethodId stored
        // to keep the payment method state in a stable state instead of the default ones
        initPaymentMethods({
          accounts: accounts as IPaymentMethod[],
          paymentMethodId: checkoutPaymentMethodId,
          prepaidPaymentMethodId: prepaidReloadPaymentMethodId
        });
      }
    }
  }, [cognitoId, defaultAccountIdentifier, userAccountsData?.userAccounts?.accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getPaymentMethods();
  }, [cognitoId, defaultAccountIdentifier, defaultReloadAmt]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading: loading || accountsLoading || prepaidLoading,
    paymentMethods,
    getPaymentMethods,
    getBalanceFromPaymentMethods,
    getPrepaidPaymentMethod,
    getPrepaidCardNumber,
    getEncryptionDetails,
    checkoutPaymentMethodId,
    setCheckoutPaymentMethodId: setAndLogCheckoutPaymentMethodId,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    setDefaultPaymentMethodId: setAndLogDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId: setAndLogDefaultReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    prepaidReloadPaymentMethodId,
    addPaymentMethod,
    addAdyenPaymentMethod,
    deletePaymentMethod,
    reloadPrepaidCard,
    mergePrepaidCardBalances,
    hasGetPaymentMethodsError,
    canUseApplePay,
    canUseGooglePay,
    paymentProcessor,
    isAdyen
  };
};

export default usePayment;
